import { renderToString } from 'react-dom/server';
import React from 'react';
import moment from 'moment-timezone';
import { find } from 'lodash';
import { isQuarter } from '@domain/_screens/overview/segmented/graph/segmented.utils';
import { METRICS_TYPES, PERCENTAGE_METRICS } from '@shared/constants/MetricsTypeConstants';
import { PERIOD_FIELDS } from '@shared/constants/Segmented.constants';
import { METRIC_SEGMENTATION_MAPPING, SEGMENTATIONS } from '@domain/_screens/overview/segmented/constants';
import { SegmentedTooltip } from './segmented-tooltip';

const isPeriodComparisonMetric = axisMetric => axisMetric
    .seriesName === PERIOD_FIELDS.previous_period.label
    || axisMetric.seriesName === PERIOD_FIELDS.same_period_last_year.label;

const constructMetricForTooltipGeneral = axisMetric => ({
    ...axisMetric,
    type: getType(axisMetric),
    label: axisMetric.seriesName,
});

const getType = (axisMetric) => {
    const isPercentage = find(PERCENTAGE_METRICS, ({ label }) => label === axisMetric.seriesName);
    if (isPercentage) return METRICS_TYPES.percent;
    return isPeriodComparisonMetric(axisMetric) ? METRICS_TYPES.comparison : METRICS_TYPES.integer;
};

const constructMetricForTooltipDate = (axisMetric, metric) => {
    const date = isPeriodComparisonMetric(axisMetric) ? metric.real_date : axisMetric.axisValue;

    const quarterLabel = metric ? metric.quarter_label : '';

    const formattedDate = moment(date).format('MM/DD/YYYY');
    return ({
        ...constructMetricForTooltipGeneral(axisMetric),
        quarterLabel,
        date: formattedDate,
    });
};

const constructMetricForTooltipHour = (axisMetric, metric) => ({
    ...constructMetricForTooltipGeneral(axisMetric),
    date: `${metric.hourofday}h`,
});

const formatMetricToAxisMetric = ({
    axisMetric,
    total,
    totalForComparison,
    segmentation,
}) => {
    if (!axisMetric) return {};
    const totalForComparisonDate = isPeriodComparisonMetric(axisMetric)
        ? totalForComparison : total;
    const metric = totalForComparisonDate[axisMetric.dataIndex];
    if (segmentation === SEGMENTATIONS.HOUR) {
        return constructMetricForTooltipHour(
            axisMetric, metric,
        );
    }
    return constructMetricForTooltipDate(axisMetric, metric);
};

export const sortByComparisonPeriod = (a, b) => {
    if (isPeriodComparisonMetric(a)) return +1;
    if (isPeriodComparisonMetric(b)) return -1;
    return 0;
};

export const whichTotalToUseDecider = (
    curveProperties,
    total,
    totalForComparison,
    segmentation,
) => curveProperties.filter((selectedMetric) => {
    const isNotHostSpot = !Array.isArray(selectedMetric.value);
    return isNotHostSpot && !Number.isNaN(parseInt(selectedMetric.value, 10));
}).map(selectedMetric => formatMetricToAxisMetric({
    axisMetric: selectedMetric,
    total,
    totalForComparison,
    segmentation,
}));

export const getRidOfEmpty = (metrics) => {
    const [firstMetric, secondMetric] = metrics;
    if (firstMetric.length === 0) return [secondMetric];
    if (secondMetric.length === 0) return [firstMetric];
    return metrics;
};

export const changeTypeIfComparison = (metrics) => {
    const [firstMetric, secondMetric] = metrics;
    if (metrics.length > 1) {
        return [firstMetric,
            {
                ...secondMetric,
                type: secondMetric.type === METRICS_TYPES.comparison
                    ? firstMetric.type : secondMetric.type,
            }];
    }
    return metrics;
};

const getQuarterLabel = (metric, segmentation, shouldBeEmpty) => {
    if (shouldBeEmpty || !isQuarter(segmentation)) {
        return '';
    }

    return metric.quarterLabel;
};

export const changeMetricsDate = (metrics, segmentation) => metrics.map((metric, index) => {
    const isNotFirstMetric = index !== 0;

    const shouldBeEmpty = isNotFirstMetric && !isPeriodComparisonMetric(metric);

    const date = shouldBeEmpty ? '' : metric.date;

    const quarterLabel = getQuarterLabel(metric, segmentation, shouldBeEmpty);

    return {
        ...metric,
        date,
        quarterLabel,
    };
});


export const formatDateBySegmentation = (date, segmentation, isComparison?) => {
    if (segmentation === SEGMENTATIONS.HOUR) return isComparison ? '' : `${date}h`;
    return date;
};

export const tooltipFormatter = (
    segmentation,
    firstData,
    secondData,
    selectedMetric,
    comparisonMetric,
    isAccumulated,
    currencySymbol?,
) => (curveProperties) => {
    const curvePropertiesWithoutHotSpot = curveProperties.filter(
        metric => !Array.isArray(metric.value),
    );

    const [firstMetric, secondMetric] = curvePropertiesWithoutHotSpot;
    const { dataIndex } = firstMetric;

    const selectedMetricData = firstData[dataIndex];
    const comparisonMetricData = secondData[dataIndex];

    const getValue = (
        withAccumulatedData,
        withoutAccumulatedData,
    ) => (isAccumulated ? withAccumulatedData : withoutAccumulatedData);

    const selectedValue = selectedMetricData[selectedMetric.metric.value];

    const comparisonValue = comparisonMetric?.metric?.type === 'comparison'
        ? comparisonMetricData[selectedMetric?.metric?.value] || ''
        : comparisonMetricData[comparisonMetric?.metric?.value] || '';

    const metrics = [
        {
            ...selectedMetric.metric,
            value: getValue(firstMetric.data, selectedValue),
            date: formatDateBySegmentation(
                selectedMetricData[METRIC_SEGMENTATION_MAPPING[segmentation]],
                segmentation,
            ),
        }, {
            ...comparisonMetric.metric,
            value: getValue(secondMetric.data, comparisonValue),
            date: formatDateBySegmentation(
                comparisonMetricData[METRIC_SEGMENTATION_MAPPING[segmentation]],
                segmentation,
                true,
            ),
        },
    ];

    const metricsWithOrWithoutSecondDate = metrics[0].date === metrics[1]?.date
        ? [metrics[0], { ...metrics[1], date: '' }]
        : [...metrics];

    const hotSpots = curveProperties.filter(
        metric => Array.isArray(metric.value),
    ).map(metric => metric.value[2]);

    return renderToString(<SegmentedTooltip
        segmentation={segmentation}
        metrics={metricsWithOrWithoutSecondDate}
        hotspots={hotSpots}
        currencySymbol={currencySymbol}
    />);
};
